import React, { useState, useEffect } from "react";
import Index from "../../../Index";
import Switch from "@mui/material/Switch";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Controller } from "react-hook-form";
import { styled } from "@mui/material/styles";
import {
  getCompanySetupDataApiHandler,
  updateCompanySetupApiHandler,
} from "../../../../services/AdminIndex";
import invoice1 from "./pdf/invoice1.pdf";
import invoice2 from "./pdf/invoice2.pdf";
import invoice3 from "./pdf/invoice3.pdf";
import invoice4 from "./pdf/invoice4.pdf";

const Item = styled(Index.Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const IOSSwitch = Index.styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 34,
  height: 20,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 3,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#114A65",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 14,
    height: 14,
    boxShadow: "none",
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

export default function CompanySetup() {
  const { state } = useLocation();
  const companyId = state?.companyId;

  const dispatch = useDispatch();

  const { loading, token } = Index.useSelector((state) => {
    return state.ERPAdminReducer;
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    watch,
  } = Index.useForm();

  const DEFAULT_COMPANY_ACCESS = {
    modules: {
      purchase: false,
      sales: false,
      accounting: false,
      process: false,
    },
    utility: {
      telly: false,
      whatsappIntegration: false,
      emailIntegration: false,
      ewaybill: false,
      einvoice: false,
    },
    Invoice1: false,
    Invoice2: false,
    Invoice3: false,
    Invoice4: false,
  };

  const [companyAccess, setCompanyAccess] = useState(DEFAULT_COMPANY_ACCESS);

  const [moduleList, setModuleList] = useState([]);
  const [assignedModuleTags, setAssignedModuleTags] = useState([]);

  const invoiceFields = ["Invoice1", "Invoice2", "Invoice3", "Invoice4"];

  const toggleModuleAssignment = (moduleTag) => {
    setAssignedModuleTags((prev) =>
      prev.includes(moduleTag)
        ? prev.filter((tag) => tag !== moduleTag)
        : [...prev, moduleTag]
    );
  };

  const handleInvoiceChange = (name, checked) => {
    if (checked) {
      const checkedCount = invoiceFields.filter(
        (field) => field !== name && watch(field)
      ).length;
      if (checkedCount >= 2) {
        toast.error("You can select maximum 2 invoice formats");
        return;
      }
    }
    setValue(name, checked);
  };

  useEffect(() => {
    getCompanySetupData();
  }, [companyId]);

  const getCompanySetupData = () => {
    try {
      getCompanySetupDataApiHandler(token, companyId).then((data) => {
        if (data.status === 201 || data.status === 200) {
          setModuleList(data.modules || []);
          setAssignedModuleTags(data.assignedModules || []);
          if (data.data?.[0]?.id) {
            setValue("Invoice1", data.data?.[0]?.invoice1);
            setValue("Invoice2", data.data?.[0]?.invoice2);
            setValue("Invoice3", data.data?.[0]?.invoice3);
            setValue("Invoice4", data.data?.[0]?.invoice4);
            let parsedAccess = {};
            try {
              parsedAccess = JSON.parse(data.data?.[0]?.access) || {};
            } catch (e) {
              parsedAccess = {};
            }
            // Merge onto defaults instead of replacing — a freshly created
            // company's access JSON only carries "assignedModules" (written
            // by CreateCompany), so a plain replace would drop the
            // modules/utility sub-objects that updateAccess("utility.x", …)
            // below assumes exist.
            setCompanyAccess({
              ...DEFAULT_COMPANY_ACCESS,
              ...parsedAccess,
              modules: { ...DEFAULT_COMPANY_ACCESS.modules, ...parsedAccess.modules },
              utility: { ...DEFAULT_COMPANY_ACCESS.utility, ...parsedAccess.utility },
            });
            setValue("ewayBillUsername", data.data?.[0]?.ewayBillUsername);
            setValue("ewayBillPassword", data.data?.[0]?.ewayBillPassword);
            setValue(
              "einvoiceBillUsername",
              data.data?.[0]?.einvoiceBillUsername
            );
            setValue(
              "einvoiceBillPassword",
              data.data?.[0]?.einvoiceBillPassword
            );
          }
        } else {
          dispatch({ type: "ADMIN_ERROR_HANDLER", payload: data });
        }
      });
    } catch (error) {
    }
  };

  const updateAccess = (propertyPath, value) => {
    setCompanyAccess((prevState) => {
      const properties = propertyPath.split(".");
      const newState = { ...prevState };
      let target = newState;
      for (let i = 0; i < properties.length - 1; i++) {
        target = target[properties[i]];
      }
      target[properties[properties.length - 1]] = value;
      return newState;
    });
  };

  const submit = (data) => {
    data.invoice1 = data.Invoice1;
    data.invoice2 = data.Invoice2;
    data.invoice3 = data.Invoice3;
    data.invoice4 = data.Invoice4;
    const accessData = {
      ...companyAccess,
      Invoice1: !!data.Invoice1,
      Invoice2: !!data.Invoice2,
      Invoice3: !!data.Invoice3,
      Invoice4: !!data.Invoice4,
      assignedModules: assignedModuleTags,
    };
    data.access = JSON.stringify(accessData);
    data.companyId = companyId;
    try {
      updateCompanySetupApiHandler(token, data).then((response) => {
        if (response.status === 201 || response.status === 200) {
          toast.success("Setting updated successfully");
        } else {
          dispatch({ type: "ADMIN_ERROR_HANDLER", payload: response });
        }
      });
    } catch (e) {
    }
  };

  return (
    <>
      <Index.Box className="main_content">
        <Index.Box className="page-title-main">
          <Index.Box className="flex-justify">
            <Index.Typography variant="h4" component="h4">
              Company Setup
            </Index.Typography>
          </Index.Box>
        </Index.Box>

        <Index.Box className="web-main-content">
          <Index.Box className="over-text-scroll">
            <form className="form-content" onSubmit={handleSubmit(submit)}>
              <Index.Box className="company-col-flex">
                <Index.Box className="common-card">
                  <Index.Box className="compnay-setup-content-main">
                    <Index.Typography className="company-setup-titles">
                      Company Modules
                    </Index.Typography>
                    {moduleList
                      .map((module) => (
                        <Index.Box className="company-setup-flex" key={module.tag}>
                          <Index.Typography className="company-switch-lable">
                            {module.title}
                          </Index.Typography>
                          <Index.Box className="company-setup-switch-main">
                            <Index.FormControlLabel
                              onChange={() => toggleModuleAssignment(module.tag)}
                              control={
                                <IOSSwitch
                                  sx={{ m: 1 }}
                                  checked={assignedModuleTags.includes(module.tag)}
                                />
                              }
                            />
                          </Index.Box>
                        </Index.Box>
                      ))}
                  </Index.Box>
                  <Index.Box className="compnay-setup-content-main">
                    <Index.Typography className="company-setup-titles">
                      Utility
                    </Index.Typography>
                    <Index.Box className="company-setup-flex">
                      <Index.Typography className="company-switch-lable">
                        Tally
                      </Index.Typography>
                      <Index.Box className="company-setup-switch-main">
                        <Index.FormControlLabel
                          onChange={() =>
                            updateAccess(
                              "utility.telly",
                              !companyAccess?.utility?.telly
                            )
                          }
                          control={
                            <IOSSwitch
                              sx={{ m: 1 }}
                              checked={companyAccess?.utility?.telly}
                            />
                          }
                        />
                      </Index.Box>
                    </Index.Box>
                    <Index.Box className="company-setup-flex">
                      <Index.Typography className="company-switch-lable">
                        Whatsapp Integration
                      </Index.Typography>
                      <Index.Box className="company-setup-switch-main">
                        <Index.FormControlLabel
                          onChange={() =>
                            updateAccess(
                              "utility.whatsappIntegration",
                              !companyAccess?.utility?.whatsappIntegration
                            )
                          }
                          control={
                            <IOSSwitch
                              sx={{ m: 1 }}
                              checked={
                                companyAccess?.utility?.whatsappIntegration
                              }
                            />
                          }
                        />
                      </Index.Box>
                    </Index.Box>
                    <Index.Box className="company-setup-flex">
                      <Index.Typography className="company-switch-lable">
                        Email Integration
                      </Index.Typography>
                      <Index.Box className="company-setup-switch-main">
                        <Index.FormControlLabel
                          onChange={() =>
                            updateAccess(
                              "utility.emailIntegration",
                              !companyAccess?.utility?.emailIntegration
                            )
                          }
                          control={
                            <IOSSwitch
                              sx={{ m: 1 }}
                              checked={
                                companyAccess?.utility?.emailIntegration
                              }
                            />
                          }
                        />
                      </Index.Box>
                    </Index.Box>
                    <Index.Box className="company-setup-flex">
                      <Index.Typography className="company-switch-lable">
                        Eway Bill
                      </Index.Typography>
                      <Index.Box className="company-setup-switch-main">
                        <Index.FormControlLabel
                          onChange={() =>
                            updateAccess(
                              "utility.ewaybill",
                              !companyAccess?.utility?.ewaybill
                            )
                          }
                          control={
                            <IOSSwitch
                              sx={{ m: 1 }}
                              checked={companyAccess?.utility?.ewaybill}
                            />
                          }
                        />
                      </Index.Box>
                    </Index.Box>
                    {companyAccess?.utility?.ewaybill && (
                      <>
                        <Index.Box sx={{ width: 1 }} className="grid-main">
                          <Index.Box
                            display="grid"
                            gridTemplateColumns="repeat(12, 1fr)"
                            gap={{ xs: 2, sm: 1, md: 2, lg: 2 }}
                          >
                            <Index.Box
                              gridColumn={{
                                xs: "span 6",
                                sm: "span 6",
                                md: "span 6",
                                lg: "span 6",
                              }}
                              className="grid-column"
                            >
                              <Index.Box className="input-box">
                                <Index.Box className="form-group">
                                  <Index.FormHelperText className="lable-control">
                                    Username
                                  </Index.FormHelperText>
                                  <Index.TextField
                                    fullWidth
                                    id="fullWidth"
                                    className="custom-form-control"
                                    value={watch("ewayBillUsername")}
                                    {...register("ewayBillUsername", {
                                      required: companyAccess?.utility?.ewaybill
                                        ? true
                                        : false,
                                    })}
                                    onChange={(e) => {
                                      setValue(
                                        "ewayBillUsername",
                                        e.target.value
                                      );
                                    }}
                                    error={Boolean(errors.ewayBillUsername)}
                                  />
                                </Index.Box>
                              </Index.Box>
                            </Index.Box>
                            <Index.Box
                              gridColumn={{
                                xs: "span 6",
                                sm: "span 6",
                                md: "span 6",
                                lg: "span 6",
                              }}
                              className="grid-column"
                            >
                              <Index.Box className="input-box">
                                <Index.Box className="form-group">
                                  <Index.FormHelperText className="lable-control">
                                    Password
                                  </Index.FormHelperText>
                                  <Index.TextField
                                    fullWidth
                                    id="fullWidth"
                                    className="custom-form-control"
                                    value={watch("ewayBillPassword")}
                                    {...register("ewayBillPassword", {
                                      required: companyAccess?.utility?.ewaybill
                                        ? true
                                        : false,
                                    })}
                                    onChange={(e) => {
                                      setValue(
                                        "ewayBillPassword",
                                        e.target.value
                                      );
                                    }}
                                    error={Boolean(errors.ewayBillPassword)}
                                  />
                                </Index.Box>
                              </Index.Box>
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      </>
                    )}
                    <Index.Box className="company-setup-flex">
                      <Index.Typography className="company-switch-lable">
                        E - Invoice
                      </Index.Typography>
                      <Index.Box className="company-setup-switch-main">
                        <Index.FormControlLabel
                          onChange={() =>
                            updateAccess(
                              "utility.einvoice",
                              !companyAccess?.utility?.einvoice
                            )
                          }
                          control={
                            <IOSSwitch
                              sx={{ m: 1 }}
                              checked={companyAccess?.utility?.einvoice}
                            />
                          }
                        />
                      </Index.Box>
                    </Index.Box>

                    {companyAccess?.utility?.einvoice && (
                      <>
                        <Index.Box
                          gridColumn={{
                            xs: "span 6",
                            sm: "span 4",
                            md: "span 2",
                            lg: "span 2",
                          }}
                          className="grid-column"
                        >
                          <Index.Box className="input-box">
                            <Index.Box className="form-group">
                              <Index.FormHelperText className="lable-control">
                                Username
                              </Index.FormHelperText>
                              <Index.TextField
                                fullWidth
                                id="fullWidth"
                                className="custom-form-control"
                                value={watch("einvoiceBillUsername")}
                                {...register("einvoiceBillUsername", {
                                  required: companyAccess?.utility?.einvoice
                                    ? true
                                    : false,
                                })}
                                onChange={(e) => {
                                  setValue(
                                    "einvoiceBillUsername",
                                    e.target.value
                                  );
                                }}
                                error={Boolean(errors.einvoiceBillUsername)}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                        <Index.Box
                          gridColumn={{
                            xs: "span 6",
                            sm: "span 4",
                            md: "span 2",
                            lg: "span 2",
                          }}
                          className="grid-column"
                        >
                          <Index.Box className="input-box">
                            <Index.Box className="form-group">
                              <Index.FormHelperText className="lable-control">
                                Password
                              </Index.FormHelperText>
                              <Index.TextField
                                fullWidth
                                id="fullWidth"
                                className="custom-form-control"
                                value={watch("einvoiceBillPassword")}
                                {...register("einvoiceBillPassword", {
                                  required: companyAccess?.utility?.einvoice
                                    ? true
                                    : false,
                                })}
                                onChange={(e) => {
                                  setValue(
                                    "einvoiceBillPassword",
                                    e.target.value
                                  );
                                }}
                                error={Boolean(errors.einvoiceBillPassword)}
                              />
                            </Index.Box>
                          </Index.Box>
                        </Index.Box>
                      </>
                    )}

                    <Index.Box className="grid-main">
                      <Index.Typography className="company-setup-titles">
                        Print Invoice
                      </Index.Typography>
                      <Index.Box
                        display="grid"
                        gridTemplateColumns="repeat(12, 1fr)"
                        className="sales-order-row"
                        gap={{ xs: 2, sm: 2, md: 2, lg: 2 }}
                      >
                        <Index.Box
                          gridColumn={{
                            xs: "span 12",
                            sm: "span 2",
                            md: "span 2",
                            lg: "span 2",
                          }}
                          className="grid-column"
                        >
                          <Item className="grid-item">
                            <Index.Box className="input-box">
                              <Index.Box className="form-group">
                                <Index.Box className="checkbox-content-flex">
                                  <Index.FormHelperText className="checkbox-lable">
                                    Invoice1
                                  </Index.FormHelperText>
                                  <Index.Box className="checkbox-main">
                                    <Controller
                                      name="Invoice1"
                                      control={control}
                                      render={({ field }) => (
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          checked={field.value}
                                          onChange={() =>
                                            handleInvoiceChange(
                                              "Invoice1",
                                              !field.value
                                            )
                                          }
                                        />
                                      )}
                                    />
                                  </Index.Box>
                                </Index.Box>
                              </Index.Box>
                            </Index.Box>
                          </Item>
                          <Index.Box className="form-group">
                            <Index.LinearButton
                              btnLabel="Preview"
                              // disabled={watch("Invoice1") ? false : true}
                              className="btn-primary"
                              onClick={() => window.open(invoice1, "_blank")}
                            ></Index.LinearButton>
                          </Index.Box>
                        </Index.Box>
                        <Index.Box
                          gridColumn={{
                            xs: "span 12",
                            sm: "span 2",
                            md: "span 2",
                            lg: "span 2",
                          }}
                          className="grid-column"
                        >
                          <Item className="grid-item">
                            <Index.Box className="input-box">
                              <Index.Box className="form-group">
                                <Index.Box className="checkbox-content-flex">
                                  <Index.FormHelperText className="checkbox-lable">
                                    Invoice2
                                  </Index.FormHelperText>
                                  <Index.Box className="checkbox-main">
                                    <Controller
                                      name="Invoice2"
                                      control={control}
                                      render={({ field }) => (
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          checked={field.value}
                                          onChange={() =>
                                            handleInvoiceChange(
                                              "Invoice2",
                                              !field.value
                                            )
                                          }
                                        />
                                      )}
                                    />
                                  </Index.Box>
                                </Index.Box>
                              </Index.Box>
                            </Index.Box>
                          </Item>
                          <Index.Box className="form-group">
                            <Index.LinearButton
                              btnLabel="Preview"
                              // disabled={watch("Invoice2") ? false : true}
                              className="btn-primary"
                              onClick={() => window.open(invoice2, "_blank")}
                            ></Index.LinearButton>
                          </Index.Box>
                        </Index.Box>
                        <Index.Box
                          gridColumn={{
                            xs: "span 12",
                            sm: "span 2",
                            md: "span 2",
                            lg: "span 2",
                          }}
                          className="grid-column"
                        >
                          <Item className="grid-item">
                            <Index.Box className="input-box">
                              <Index.Box className="form-group">
                                <Index.Box className="checkbox-content-flex">
                                  <Index.FormHelperText className="checkbox-lable">
                                    Invoice3
                                  </Index.FormHelperText>
                                  <Index.Box className="checkbox-main">
                                    <Controller
                                      name="Invoice3"
                                      control={control}
                                      render={({ field }) => (
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          checked={field.value}
                                          onChange={() =>
                                            handleInvoiceChange(
                                              "Invoice3",
                                              !field.value
                                            )
                                          }
                                        />
                                      )}
                                    />
                                  </Index.Box>
                                </Index.Box>
                              </Index.Box>
                            </Index.Box>
                          </Item>
                          <Index.Box className="form-group">
                            <Index.LinearButton
                              btnLabel="Preview"
                              // disabled={watch("Invoice3") ? false : true}
                              className="btn-primary"
                              onClick={() => window.open(invoice3, "_blank")}
                            ></Index.LinearButton>
                          </Index.Box>
                        </Index.Box>
                        <Index.Box
                          gridColumn={{
                            xs: "span 12",
                            sm: "span 2",
                            md: "span 2",
                            lg: "span 2",
                          }}
                          className="grid-column"
                        >
                          <Item className="grid-item">
                            <Index.Box className="input-box">
                              <Index.Box className="form-group">
                                <Index.Box className="checkbox-content-flex">
                                  <Index.FormHelperText className="checkbox-lable">
                                    Invoice4
                                  </Index.FormHelperText>
                                  <Index.Box className="checkbox-main">
                                    <Controller
                                      name="Invoice4"
                                      control={control}
                                      render={({ field }) => (
                                        <input
                                          type="checkbox"
                                          className="checkbox"
                                          checked={field.value}
                                          onChange={() =>
                                            handleInvoiceChange(
                                              "Invoice4",
                                              !field.value
                                            )
                                          }
                                        />
                                      )}
                                    />
                                  </Index.Box>
                                </Index.Box>
                              </Index.Box>
                            </Index.Box>
                          </Item>
                          <Index.Box className="form-group">
                            <Index.LinearButton
                              btnLabel="Preview"
                              // disabled={watch("Invoice4") ? false : true}
                              className="btn-primary"
                              onClick={() => window.open(invoice4, "_blank")}
                            ></Index.LinearButton>
                          </Index.Box>
                        </Index.Box>
                      </Index.Box>
                    </Index.Box>
                  </Index.Box>
                </Index.Box>
              </Index.Box>
              <Index.Box className="btn-flex-end">
                <Index.LinearButton
                  btnLabel="Save"
                  className="btn-primary"
                  type="submit"
                ></Index.LinearButton>
              </Index.Box>
            </form>
          </Index.Box>
        </Index.Box>
      </Index.Box>
    </>
  );
}
